import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  FlaskConical,
  Files,
  Globe,
  Clock,
  FileText,
  History,
  Home,
  LogOut,
  Menu as MenuIcon,
  MessagesSquare,
  PlugZap,
  Tags,
  Users,
  X,
} from 'lucide-react';
import { WS_ROLE_LABELS } from '@zentera/shared';
import { cn, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@zentera/ui';
import { api } from '@zentera/web-core';
import { meKey, useCan, useMe } from '../lib/auth';

const TitleContext = createContext<(t: string) => void>(() => {});
export function usePageTitle(title: string) {
  const set = useContext(TitleContext);
  useEffect(() => {
    set(title);
    document.title = `${title} · Zentera Workspace`;
  }, [title, set]);
}

type Item = {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  soon?: string;
  permission?: 'team.read' | 'audit.read' | 'assistant.test' | 'settings.write' | 'conversations.read' | 'dashboard.read';
};
const NAV: { group?: string; items: Item[] }[] = [
  { items: [{ to: '/', label: 'Início', icon: <Home />, end: true }] },
  {
    group: 'Atendimento',
    items: [
      { to: '/dashboard', label: 'Painel', icon: <BarChart3 />, permission: 'dashboard.read' },
      { to: '/conversations', label: 'Conversas', icon: <MessagesSquare />, permission: 'conversations.read' },
      { to: '/integration', label: 'Integração do agente', icon: <PlugZap />, permission: 'settings.write' },
    ],
  },
  {
    group: 'Conhecimento',
    items: [
      { to: '/institution', label: 'Dados institucionais', icon: <Building2 /> },
      { to: '/plans', label: 'Planos', icon: <Tags /> },
      { to: '/hours', label: 'Horários', icon: <Clock /> },
      { to: '/policies', label: 'Políticas', icon: <FileText /> },
      { to: '/faq', label: 'Perguntas frequentes', icon: <BookOpen /> },
      { to: '/documents', label: 'Documentos', icon: <Files /> },
      { to: '/website', label: 'Site', icon: <Globe /> },
    ],
  },
  {
    group: 'Assistente',
    items: [
      { to: '/assistant', label: 'Publicação e estado', icon: <Bot />, end: true },
      { to: '/assistant/test', label: 'Testar assistente', icon: <FlaskConical />, permission: 'assistant.test' },
    ],
  },
  {
    group: 'Administração',
    items: [
      { to: '/team', label: 'Equipe', icon: <Users />, permission: 'team.read' },
      { to: '/activity', label: 'Atividade', icon: <History />, permission: 'audit.read' },
    ],
  },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const can = useCan();
  const { data: inst } = useQuery({ queryKey: ['institution'], queryFn: () => api.get<{ tradeName: string } | null>('/api/institution') });
  return (
    <nav aria-label="Principal" className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center gap-2 px-4">
        <img src="/zentera-mark.svg" alt="" className="size-5" />
        <span className="truncate text-[14px] font-semibold tracking-[-0.01em]">{inst?.tradeName ?? 'Workspace'}</span>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto px-2 pt-3 pb-4">
        {NAV.map((g, gi) => {
          const items = g.items.filter((i) => !i.permission || can(i.permission));
          if (!items.length) return null;
          return (
            <div key={gi}>
              {g.group && <div className="px-2 pb-1 text-2xs font-medium tracking-wide text-fg-4 uppercase">{g.group}</div>}
              <ul className="space-y-px">
                {items.map((it) =>
                  it.soon ? (
                    <li key={it.to} className="flex h-7 items-center gap-2.5 px-2 text-sm text-fg-4 [&_svg]:size-[15px]" aria-disabled="true">
                      {it.icon}
                      <span className="flex-1">{it.label}</span>
                      <span className="text-2xs">{it.soon}</span>
                    </li>
                  ) : (
                    <li key={it.to}>
                      <NavLink
                        to={it.to}
                        end={it.end}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          cn(
                            'flex h-7 items-center gap-2.5 rounded-md px-2 text-sm transition-colors duration-75 [&_svg]:size-[15px] [&_svg]:shrink-0',
                            isActive ? 'bg-bg font-medium text-fg shadow-xs ring-1 ring-line [&_svg]:text-brand' : 'text-fg-2 hover:bg-muted hover:text-fg [&_svg]:text-fg-3',
                          )
                        }
                      >
                        {it.icon}
                        {it.label}
                      </NavLink>
                    </li>
                  ),
                )}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="border-t border-line px-4 py-3 text-2xs text-fg-4">Zentera Workspace</div>
    </nav>
  );
}

function UserMenu() {
  const { data } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSettled: () => {
      qc.clear();
      void qc.invalidateQueries({ queryKey: meKey });
      void navigate('/login');
    },
  });
  if (!data) return null;
  return (
    <Menu>
      <MenuTrigger className="flex items-center gap-2 rounded-md py-1 pr-1.5 pl-1 hover:bg-muted" aria-label="Conta">
        <span className="flex size-6 items-center justify-center rounded-full bg-fg text-2xs font-medium text-white">
          {data.user.name.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden text-sm text-fg-2 md:inline">{data.user.name}</span>
      </MenuTrigger>
      <MenuContent>
        <MenuLabel>
          <div className="text-sm font-medium text-fg">{data.user.name}</div>
          <div>{data.user.email}</div>
          <div className="mt-1">{WS_ROLE_LABELS[data.user.role]}</div>
        </MenuLabel>
        <MenuSeparator />
        <MenuItem onSelect={() => void navigate('/password')}>Alterar senha</MenuItem>
        <MenuItem icon={<LogOut />} onSelect={() => logout.mutate()}>Sair</MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function Shell() {
  const [title, setTitle] = useState('');
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  return (
    <TitleContext.Provider value={setTitle}>
      <div className="flex min-h-screen bg-bg">
        <aside className="sticky top-0 hidden h-screen w-[232px] shrink-0 border-r border-line bg-subtle lg:block">
          <Sidebar />
        </aside>
        {open && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 animate-fade-in bg-fg/20" onClick={() => setOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-[260px] animate-pop-in border-r border-line bg-subtle shadow-pop">
              <button className="absolute top-3 right-3 rounded-md p-1 text-fg-3 hover:bg-muted" onClick={() => setOpen(false)} aria-label="Fechar menu">
                <X className="size-4" />
              </button>
              <Sidebar onNavigate={() => setOpen(false)} />
            </aside>
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-3 border-b border-line bg-bg/95 px-4 backdrop-blur-sm lg:px-6">
            <button className="-ml-1 rounded-md p-1.5 text-fg-2 hover:bg-muted lg:hidden" onClick={() => setOpen(true)} aria-label="Abrir menu">
              <MenuIcon className="size-4" />
            </button>
            <Link to="/" className="hidden text-sm text-fg-3 hover:text-fg sm:inline">Workspace</Link>
            {title && <span className="hidden text-fg-4 sm:inline">/</span>}
            <span className="flex-1 truncate text-sm font-medium">{title}</span>
            <UserMenu />
          </header>
          <main className="min-w-0 flex-1 px-4 py-6 lg:px-8 lg:py-7">
            <div className="mx-auto max-w-[1100px]">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </TitleContext.Provider>
  );
}
